import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { BookOpenCheck } from 'lucide-react'
import { BookReservationItem } from '@/components/BookReservationItem'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/hooks/useAuth'
import { useLoans } from '@/hooks/useLoans'

interface ReservationListItem {
  key: string
  title: string
  thumbnailUrl: string | null
  author: string
  status: 'on-loan' | 'borrowed' | 'returned'
  lastAction: string
  lastActionAt: number
}

const formatActionDate = (value: string | null) => {
  if (!value) {
    return '-'
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(value))
}

export const Home = () => {
  const { user } = useAuth()
  const { activeLoans, historyLoans, loading: loansLoading } = useLoans(user?.id)

  const loanSummary = useMemo(() => {
    return activeLoans.reduce(
      (counts, loan) => {
        if (loan.loan_type === 'on-loan') {
          counts.onLoan += 1
        } else {
          counts.borrowed += 1
        }

        return counts
      },
      { onLoan: 0, borrowed: 0 }
    )
  }, [activeLoans])

  const statusSummary = useMemo(() => {
    const parts: string[] = []

    if (loanSummary.onLoan > 0) {
      parts.push(`${loanSummary.onLoan} ${loanSummary.onLoan === 1 ? 'book' : 'books'} on loan`)
    }

    if (loanSummary.borrowed > 0) {
      parts.push(
        `${loanSummary.borrowed} ${loanSummary.borrowed === 1 ? 'book' : 'books'} borrowed`
      )
    }

    return parts.join(', ')
  }, [loanSummary.borrowed, loanSummary.onLoan])

  const recentActivityItems = useMemo<ReservationListItem[]>(() => {
    const activeItems = activeLoans.map((loan) => {
      const loanedAt = new Date(loan.loaned_at).getTime()
      const status = loan.loan_type
      const actionLabel =
        loan.loan_type === 'on-loan'
          ? `Loaned to ${loan.friend.name} on ${formatActionDate(loan.loaned_at)}`
          : `Borrowed from ${loan.friend.name} on ${formatActionDate(loan.loaned_at)}`

      return {
        key: `active-${loan.id}`,
        title: loan.book.title,
        thumbnailUrl: loan.book.cover_url,
        author: loan.book.authors[0] ?? 'Unknown author',
        status,
        lastAction: actionLabel,
        lastActionAt: loanedAt,
      }
    })

    const returnedItems = historyLoans.map((loan) => {
      const returnedAt = loan.returned_at ? new Date(loan.returned_at).getTime() : 0
      const returnLabel =
        loan.loan_type === 'on-loan'
          ? `Returned by ${loan.friend.name} on ${formatActionDate(loan.returned_at)}`
          : `Returned to ${loan.friend.name} on ${formatActionDate(loan.returned_at)}`

      return {
        key: `returned-${loan.id}`,
        title: loan.book.title,
        thumbnailUrl: loan.book.cover_url,
        author: loan.book.authors[0] ?? 'Unknown author',
        status: 'returned' as const,
        lastAction: returnLabel,
        lastActionAt: returnedAt,
      }
    })

    return [...activeItems, ...returnedItems]
      .sort((left, right) => right.lastActionAt - left.lastActionAt)
      .slice(0, 5)
  }, [activeLoans, historyLoans])

  return (
    <main className='mx-auto grid w-full max-w-5xl gap-4 p-3 md:grid-cols-2 md:gap-6 md:p-6'>
      <div className='md:col-span-2 md:flex md:justify-center'>
        <Button asChild size='lg' className='w-full md:w-auto'>
          <Link to='/move-book'>
            <BookOpenCheck className='h-5 w-5' />
            Find a book
          </Link>
        </Button>
      </div>

      {statusSummary && (
        <Card className='md:col-span-2'>
          <CardHeader>
            <CardTitle>Status</CardTitle>
            <CardDescription>Active loans and borrows.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className='text-2xl font-semibold'>{statusSummary}</p>
          </CardContent>
        </Card>
      )}

      <Card className='md:col-span-2'>
        <CardHeader>
          <CardTitle>Recent activity</CardTitle>
          <CardDescription>Last 5 things that happened.</CardDescription>
        </CardHeader>
        <CardContent className='space-y-3'>
          {loansLoading && <p className='text-sm text-muted-foreground'>Loading...</p>}
          {!loansLoading && recentActivityItems.length === 0 && (
            <p className='text-sm text-muted-foreground'>No booking activity yet.</p>
          )}
          {recentActivityItems.map((item) => (
            <BookReservationItem
              key={item.key}
              title={item.title}
              thumbnailUrl={item.thumbnailUrl}
              author={item.author}
              status={item.status}
              lastAction={item.lastAction}
            />
          ))}
        </CardContent>
      </Card>
    </main>
  )
}
